import axios from 'axios';
import toast from 'react-hot-toast';

// For development subdirectory, always use /api (not /rayyi7-dev/api)
const API_BASE_URL = process.env.REACT_APP_API_URL || 
  (window.location.hostname === 'localhost' 
    ? 'http://localhost:3026/api' 
    : '/api');

// Create axios instance
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token refresh state
let isRefreshing = false;
let failedQueue: any[] = [];

// Track if this is the first load after login
let isInitialLoad = true;
setTimeout(() => {
  isInitialLoad = false;
}, 5000); // Give 5 seconds for initial load

// Reset on login
window.addEventListener('userLoggedIn', () => {
  isInitialLoad = true;
  setTimeout(() => {
    isInitialLoad = false;
  }, 5000);
});

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    console.log('API Request:', config.url, 'Token exists:', !!token);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log('API Request: Token added to headers');
    } else {
      console.log('API Request: No token found in localStorage');
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Don't log 401 errors during initial load to reduce noise
    if (!isInitialLoad || error.response?.status !== 401) {
      console.log('API Response Error:', {
        url: originalRequest?.url,
        status: error.response?.status,
        message: error.response?.data?.message || error.message,
        isRetry: originalRequest?._retry
      });
    }
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Don't retry auth endpoints
      if (originalRequest.url?.includes('/auth/')) {
        console.log('401 on auth endpoint, not triggering auto-logout');
        return Promise.reject(error);
      }

      if (isRefreshing) {
        console.log('Token refresh already in progress, queuing request:', originalRequest.url);
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return apiClient(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;
      
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        isRefreshing = false;
        processQueue(new Error('No refresh token'), null);
        
        // Clear auth and redirect
        if (!window.location.pathname.includes('login')) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('user');
          
          setTimeout(() => {
            const basename = window.location.pathname.startsWith('/rayyi7-dev') ? '/rayyi7-dev' : 
                             window.location.pathname.startsWith('/dev') ? '/dev' : '';
            window.location.href = basename + '/login';
          }, 100);
        }
        return Promise.reject(error);
      }

      console.log('Attempting token refresh for 401 error');
      
      try {
        const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
          refresh_token: refreshToken,
        });
        
        const { access_token } = response.data;
        localStorage.setItem('access_token', access_token);
        
        console.log('Token refresh successful, processing queued requests:', failedQueue.length);
        
        // Process queued requests
        processQueue(null, access_token);
        
        // Reset the refreshing flag before retrying
        isRefreshing = false;
        
        // Retry original request with new token
        originalRequest.headers.Authorization = `Bearer ${access_token}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        console.log('API Interceptor: Token refresh failed:', refreshError);
        processQueue(refreshError, null);
        isRefreshing = false;
        
        // Only auto-logout if we're not on the login page
        if (!window.location.pathname.includes('login')) {
          console.log('API Interceptor: Clearing auth data and redirecting to login');
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('user');
          
          // Use a slight delay to ensure auth context updates
          setTimeout(() => {
            const basename = window.location.pathname.startsWith('/rayyi7-dev') ? '/rayyi7-dev' : 
                             window.location.pathname.startsWith('/dev') ? '/dev' : '';
            window.location.href = basename + '/login';
          }, 100);
        } else {
          console.log('API Interceptor: Not clearing auth data - on login page');
        }
        
        return Promise.reject(refreshError);
      }
    }
    
    // Show error message (but not for auth-related errors during login, 401 errors, or during initial load)
    const shouldShowError = error.response?.status !== 401 && 
                           !originalRequest.url?.includes('/auth/') &&
                           (!isInitialLoad || error.response?.status >= 500);
                           
    if (shouldShowError && error.response?.data?.error) {
      // Ensure we always pass a string to toast.error
      const errorMessage = typeof error.response.data.error === 'string' 
        ? error.response.data.error 
        : error.response.data.error.message || 'An error occurred';
      toast.error(errorMessage);
    } else if (shouldShowError && error.message) {
      toast.error(error.message);
    }
    
    return Promise.reject(error);
  }
);

export default apiClient;